import { sum } from 'es-toolkit';
import React, { useState } from 'react';
import { Box, InputLabel, TextField, Typography } from '@mui/material';

import { enqueueError } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { createOrAddToBundleFromQuantitiesByAnalysisSetGuid } from '../../actions/bundleActions';
import { getInitialAndMaxQuantitiesForBundleCreationOrEditing } from '../../selectors/bundles';
import { getDistanceFormatterSelector } from '../../selectors/getDistanceFormatterSelector';
import { getOrderedCheckedAnalysisSetGuidsForUnbundled } from '../../selectors/productionGroupGrid';
import { getLoadedTrussesByAnalysisSetGuid } from '../../selectors/trusses';
import { useAppDispatch, useAppSelector } from '../hooks';

export function CreateOrAddToBundleQuantitiesDialog() {
  const dispatch = useAppDispatch();
  const orderedCheckedAnalysisSetGuidsForUnbundled = useAppSelector(getOrderedCheckedAnalysisSetGuidsForUnbundled);
  const loadedTrussesByAnalysisSetGuid = useAppSelector(getLoadedTrussesByAnalysisSetGuid);
  const bundleBeingEditedGuid = useAppSelector((state) => state.batchesAndBundlesTab.bundleBeingEditedGuid);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  const { initialQuantities, maxQuantities } = useAppSelector(getInitialAndMaxQuantitiesForBundleCreationOrEditing);
  const [quantitiesForBundle, setQuantitiesForBundle] = useState<{ [analysisSetGuid: string]: number }>(
    initialQuantities,
  );
  const allQuantitiesSpecified = quantitiesForBundle && !Object.values(quantitiesForBundle).some(Number.isNaN);

  return (
    <Dialog
      title="Bundle Quantities"
      open
      submitText={bundleBeingEditedGuid !== null ? 'add' : 'create'}
      onSubmit={() => {
        dispatch(createOrAddToBundleFromQuantitiesByAnalysisSetGuid(quantitiesForBundle, bundleBeingEditedGuid));
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '400px' }}
      enterKeySubmits
    >
      <Typography>
        Total Thickness:{' '}
        {allQuantitiesSpecified &&
          distanceFormatter(
            sum(
              Object.entries(quantitiesForBundle).map(
                ([analysisSetGuid, quantity]) =>
                  loadedTrussesByAnalysisSetGuid[analysisSetGuid].thickness *
                  quantity *
                  loadedTrussesByAnalysisSetGuid[analysisSetGuid].numberOfPlies,
              ),
            ),
          )}
      </Typography>
      <table>
        <tbody>
          {orderedCheckedAnalysisSetGuidsForUnbundled.map((analysisSetGuid) => (
            <tr key={analysisSetGuid}>
              <Box component="td" sx={{ p: 1 }}>
                <InputLabel sx={{ verticalAlign: 'middle' }}>
                  {loadedTrussesByAnalysisSetGuid[analysisSetGuid].name}
                </InputLabel>
              </Box>
              <Box component="td" sx={{ p: 1 }}>
                <TextField
                  variant="standard"
                  value={quantitiesForBundle[analysisSetGuid]}
                  onChange={(event) => {
                    const newQuantity = parseInt(event.target.value, 10);
                    const maxQuantity = maxQuantities[analysisSetGuid];
                    if (maxQuantity === undefined) throw new Error('Max quantity not found');
                    if (newQuantity > maxQuantity) {
                      dispatch(
                        enqueueError(
                          `Quantity for ${loadedTrussesByAnalysisSetGuid[analysisSetGuid].name} should not be greater than ${maxQuantity}`,
                        ),
                      );
                      return;
                    }
                    setQuantitiesForBundle((state) => ({ ...state, [analysisSetGuid]: newQuantity }));
                  }}
                  type="number"
                  // Prevent changing the number value with the scroll wheel
                  onWheel={(event) => (event.target as HTMLInputElement).blur()}
                />
              </Box>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
}
